import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import { localStorageAsync } from "#app/lib/local-storage";

function readSync<T extends object>(key: string, defaults: T): T {
  if (typeof window === "undefined") return defaults;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return defaults;
    return { ...defaults, ...JSON.parse(raw) };
  } catch {
    return defaults;
  }
}

/**
 * A patch, or a function handed the CURRENT state that returns one. The
 * functional form is load-bearing for stores whose handlers write twice in one
 * event (create a row, then link it): the second write must see the first, and
 * mapping over the array captured by this render would drop it.
 */
export type PersistedStatePatch<T extends object> =
  | Partial<T>
  | ((current: T) => Partial<T>);

type StoreEntry<T extends object> = {
  state: T;
  listeners: Set<() => void>;
};

// One entry per storage key, shared by every hook reading it. `state` is
// replaced (never mutated) on write, so it doubles as the snapshot identity
// useSyncExternalStore compares.
const entries = new Map<string, StoreEntry<object>>();

function entryFor<T extends object>(key: string, defaults: T): StoreEntry<T> {
  const existing = entries.get(key);
  if (existing) return existing as StoreEntry<T>;
  const created: StoreEntry<T> = {
    listeners: new Set(),
    state: readSync(key, defaults),
  };
  entries.set(key, created as StoreEntry<object>);
  return created;
}

function writeEntry<T extends object>(
  key: string,
  defaults: T,
  patch: PersistedStatePatch<T>,
) {
  const entry = entryFor(key, defaults);
  entry.state = {
    ...entry.state,
    ...(typeof patch === "function" ? patch(entry.state) : patch),
  };
  localStorageAsync.setItem(key, JSON.stringify(entry.state)).catch(() => {});
  for (const listener of entry.listeners) listener();
}

/** Forgets every key's in-memory state, for a test reset: the entries are
 *  module state, so they outlive a cleared `localStorage` within one file. */
export function resetPersistedStateEntries(): void {
  entries.clear();
}

/** Drops a key's stored state, for a record whose owner is gone (a deleted
 *  widget). A hook still mounted on the key keeps the same entry, now empty,
 *  so its later writes still reach its listeners; with no hook mounted the
 *  entry goes too. An empty state reads as each key's default. */
export function removePersistedState(key: string): void {
  localStorageAsync.removeItem(key).catch(() => {});
  const entry = entries.get(key);
  if (!entry) return;
  if (entry.listeners.size === 0) {
    entries.delete(key);
    return;
  }
  entry.state = {};
  for (const listener of entry.listeners) listener();
}

/**
 * Generic hook for state persisted to localStorage. The initial state is
 * seeded synchronously so the first paint already reflects the persisted
 * value — no flicker from defaults to stored value.
 *
 * **Every hook on the same key reads and writes ONE shared state.** A store
 * with two hosts — a route holding the list beside a panel holding the
 * mutations, a detail dialog open over the table it was opened from — is the
 * normal shape, and per-instance `useState` would leave each host rendering
 * its own stale copy of a value they both just wrote. The module entry plus
 * `useSyncExternalStore` is what makes a write visible everywhere at once —
 * the recents stores (`use-recent-items.ts`, `use-recent-dashboards.ts`)
 * depend on exactly that: a detail overlay records and a widget on the same
 * screen lists.
 *
 * `defaults` seeds the key the first time any hook mounts on it; later hooks
 * join the state already there. Dynamic keys are supported: when `key` changes
 * (e.g. a workspace-scoped key after switching workspaces), the hook reads
 * that key's entry from the next render — no flash of the previous key's
 * state.
 */
export function usePersistedState<T extends object>(
  key: string,
  defaults: T,
): {
  state: T;
  update: (patch: PersistedStatePatch<T>) => void;
} {
  // Defaults are read only to seed a key nobody has touched yet, and call
  // sites hand them over as a fresh literal each render — so they ride a ref
  // rather than a dependency, which would resubscribe on every render.
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const entry = entryFor(key, defaultsRef.current);
      entry.listeners.add(onStoreChange);
      return () => {
        entry.listeners.delete(onStoreChange);
      };
    },
    [key],
  );

  const getSnapshot = useCallback(
    () => entryFor(key, defaultsRef.current).state,
    [key],
  );

  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const update = useCallback(
    (patch: PersistedStatePatch<T>) => writeEntry(key, defaultsRef.current, patch),
    [key],
  );

  return useMemo(() => ({ state, update }), [state, update]);
}
